import { Link } from "react-router-dom";
import { AlertTriangle, Building2, CalendarClock, Phone, User } from "lucide-react";
import type { Company } from "@/components/api/companiesApi";
import { isOverdue, needsNextAction, officeCountLabel, primaryContact } from "@/lib/cms";
import { formatEuroDate } from "@/lib/date";
import { cn } from "@/lib/utils";
import { StatusBadge } from "./StatusBadge";
import { StyleSwatch } from "./StyleSwatch";

interface CompanyCardProps {
    company: Company;
}

export function CompanyCard({ company }: CompanyCardProps) {
    const overdue = isOverdue(company);
    const needsAction = needsNextAction(company);
    const primary = primaryContact(company.contacts);

    return (
        <Link
            to={`/cms/${company.id}`}
            className={cn(
                "glass-card p-5 flex flex-col gap-4 transition-all duration-300 hover:border-primary/50",
                overdue && "ring-1 ring-destructive/60",
            )}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h3 className="font-semibold truncate">{company.name}</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                        {company.lastContacted
                            ? `Last contacted ${formatEuroDate(company.lastContacted)}`
                            : "Never contacted"}
                    </p>
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <StatusBadge status={company.status} />
                    {company.officeCount != null && (
                        <span
                            className="inline-flex items-center gap-1 text-xs text-muted-foreground"
                            title={`${officeCountLabel(company.officeCount)} office${
                                company.officeCount === 1 ? "" : "s"
                            }`}
                        >
                            <Building2 className="h-3.5 w-3.5 shrink-0" />
                            {officeCountLabel(company.officeCount)}
                        </span>
                    )}
                </div>
            </div>

            {/* Next action */}
            <div className="text-sm">
                {company.nextAction ? (
                    <div className="flex items-start gap-2">
                        <CalendarClock className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                        <div className="min-w-0">
                            <p className="truncate">{company.nextAction.description}</p>
                            <p
                                className={cn(
                                    "text-xs mt-0.5",
                                    overdue ? "text-destructive font-medium" : "text-muted-foreground",
                                )}
                            >
                                Due {formatEuroDate(company.nextAction.dueDate)}
                                {overdue && " · Overdue"}
                            </p>
                        </div>
                    </div>
                ) : needsAction ? (
                    <div className="flex items-center gap-2 text-amber-300">
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        <span className="text-xs font-medium">Needs a next action</span>
                    </div>
                ) : (
                    <p className="text-xs text-muted-foreground">No next action</p>
                )}
            </div>

            {/* Footer: primary contact + style swatch */}
            <div className="mt-auto flex items-end justify-between gap-3 pt-1">
                <div className="min-w-0 text-xs text-muted-foreground">
                    {primary ? (
                        <>
                            <p className="flex items-center gap-1.5 text-foreground truncate">
                                <User className="h-3.5 w-3.5 shrink-0" />
                                <span className="truncate">{primary.name}</span>
                            </p>
                            {primary.phone && (
                                <p className="flex items-center gap-1.5 mt-1 truncate">
                                    <Phone className="h-3.5 w-3.5 shrink-0" />
                                    <span className="truncate">{primary.phone}</span>
                                </p>
                            )}
                        </>
                    ) : (
                        <span>No contact yet</span>
                    )}
                </div>
                <StyleSwatch
                    primaryColor={company.style.primaryColor}
                    secondaryColor={company.style.secondaryColor}
                />
            </div>
        </Link>
    );
}
